// DC DPR spray park / splash pad source catalog. Generated from
// research/splash-pads-2026.json (DPR 2026 season list; coordinates are
// Census-geocoded via US Census Bureau Public_AR_Current benchmark, 2026-06-13).
//
// This is the DATA only. The seed (shared/seed-pools.ts) turns each entry into a
// crowd-reportable Pool (kind:"splash", poolId:"splash-<id>"); the Splash tab
// reads this array for per-pad maintenance-window text + the season note.
// Lives in shared/ (not src/) so the seed can import it without pulling in
// frontend code; re-exported from src/splash-pads.ts for the views.
//
// 35 active spray parks across wards 1-7 (DPR lists 0 in ward 8 for 2026 — a known
// data gap, not fabricated here). Closed/bonus non-DPR locations from the research
// file are intentionally omitted.

import type { SplashPad, SplashSeason } from "./types";

/** Season / hours note shown at the top of the Splash Pads tab. */
export const SPLASH_SEASON: SplashSeason = {
  typical: "Memorial Day weekend through Labor Day weekend",
  hours: "10:00 AM – 6:00 PM",
  schedule2026: "Weekends only May 23 – June 21, 2026; daily (10 AM–6 PM) starting June 22, 2026",
  notes: "Some locations have a weekly off window for maintenance; hours are noted in each listing. Canal Park and Franklin Park are operated by BIDs and may have different hours.",
  // The two phases above, in machine-readable form. Same dates the seed already
  // used for its SeasonWindows (it now imports these), so there is exactly one
  // copy of the 2026 season calendar in the repo.
  weekendsOnly: { start: "2026-05-23", end: "2026-06-21" },
  daily: { start: "2026-06-22", end: "2026-09-07" },
};

/** The 35 DPR spray parks. Render sorted by ward then name. */
export const SPLASH_PADS: SplashPad[] = [
  {
    id: "14th-girard-spray",
    name: "14th and Girard Street Spray Park",
    address: "14th and Girard Street NW, Washington, DC 20009",
    ward: 1,
    lat: 38.92574,
    lng: -77.03234,
    confidence: "high",
  },
  {
    id: "columbia-heights-community-center-spray",
    name: "Columbia Heights Community Center Spray Park",
    address: "1480 Girard Street NW, Washington, DC 20009",
    ward: 1,
    lat: 38.92529,
    lng: -77.03480,
    confidence: "high",
  },
  {
    id: "columbia-heights-civic-plaza-spray",
    name: "Columbia Heights Civic Plaza Spray Park",
    address: "14th Street & Park Road NW, Washington, DC 20010",
    ward: 1,
    lat: 38.93017,
    lng: -77.03266,
    maintenanceWindow: "Wednesdays 10am–2pm",
    confidence: "high",
  },
  {
    id: "harrison-rec-spray",
    name: "Harrison Recreation Center Spray Park",
    address: "1330 V Street NW, Washington, DC 20009",
    ward: 1,
    lat: 38.91806,
    lng: -77.03036,
    maintenanceWindow: "Thursdays 2–6pm",
    confidence: "high",
  },
  {
    id: "ledriot-park-spray",
    name: "Park at LeDroit Spray Park",
    address: "319 Oakdale Place NW, Washington, DC 20001",
    ward: 1,
    lat: 38.91796,
    lng: -77.01713,
    maintenanceWindow: "Fridays 2–6pm",
    confidence: "medium",
  },
  {
    id: "westminster-playground-spray",
    name: "Westminster Playground Spray Park",
    address: "911 Westminster Street NW, Washington, DC",
    ward: 1,
    lat: 38.91489,
    lng: -77.02423,
    confidence: "medium",
  },
  {
    id: "kennedy-rec-spray",
    name: "Kennedy Recreation Center Spray Park",
    address: "1401 7th Street NW, Washington, DC 20001",
    ward: 2,
    lat: 38.90862,
    lng: -77.02184,
    maintenanceWindow: "Tuesdays 10am–2pm",
    confidence: "high",
  },
  {
    id: "stead-park-spray",
    name: "Stead Park Recreation Center Spray Park",
    address: "1625 P Street NW, Washington, DC 20036",
    ward: 2,
    lat: 38.90970,
    lng: -77.03704,
    maintenanceWindow: "Wednesdays 2–6pm",
    confidence: "high",
  },
  {
    id: "franklin-park-spray",
    name: "Franklin Park Spray Park",
    address: "1315 I Street NW, Washington, DC 20005",
    ward: 2,
    lat: 38.90139,
    lng: -77.03001,
    maintenanceWindow: "Operated by Downtown DC BID; see downtowndc.org for hours",
    confidence: "high",
  },
  {
    id: "chevy-chase-rec-spray",
    name: "Chevy Chase Recreation Center Spray Park",
    address: "5500 41st Street NW, Washington, DC 20015",
    ward: 3,
    lat: 38.96315,
    lng: -77.08036,
    maintenanceWindow: "Mondays 2–6pm",
    confidence: "high",
  },
  {
    id: "friendship-rec-spray",
    name: "Friendship Recreation Center Spray Park",
    address: "4500 Van Ness Street NW, Washington, DC 20016",
    ward: 3,
    lat: 38.94316,
    lng: -77.08863,
    maintenanceWindow: "Wednesdays 10am–2pm",
    confidence: "high",
  },
  {
    id: "guy-mason-rec-spray",
    name: "Guy Mason Recreation Center Spray Park",
    address: "3600 Calvert Street NW, Washington, DC 20007",
    ward: 3,
    lat: 38.92249,
    lng: -77.07096,
    confidence: "high",
  },
  {
    id: "hardy-rec-spray",
    name: "Hardy Recreation Center Spray Park",
    address: "4500 Q Street NW, Washington, DC 20007",
    ward: 3,
    lat: 38.91008,
    lng: -77.08508,
    maintenanceWindow: "Thursdays 10am–2pm",
    confidence: "medium",
  },
  {
    id: "macomb-rec-spray",
    name: "Macomb Recreation Center Spray Park",
    address: "3409 Macomb Street NW, Washington, DC 20016",
    ward: 3,
    lat: 38.93355,
    lng: -77.06664,
    maintenanceWindow: "Mondays 10am–2pm",
    confidence: "high",
  },
  {
    id: "palisades-community-center-spray",
    name: "Palisades Community Center Spray Park",
    address: "5200 Sherier Place NW, Washington, DC 20016",
    ward: 3,
    lat: 38.92619,
    lng: -77.10438,
    maintenanceWindow: "Mondays 2–6pm",
    confidence: "high",
  },
  {
    id: "fort-stevens-rec-spray",
    name: "Fort Stevens Recreation Center Spray Park",
    address: "1327 Van Buren Street NW, Washington, DC 20012",
    ward: 4,
    lat: 38.96999,
    lng: -77.03070,
    confidence: "high",
  },
  {
    id: "lafayette-pointer-rec-spray",
    name: "Lafayette-Pointer Recreation Center Spray Park",
    address: "5900 33rd Street NW, Washington, DC 20015",
    ward: 4,
    lat: 38.96766,
    lng: -77.06645,
    confidence: "high",
  },
  {
    id: "petworth-rec-spray",
    name: "Petworth Recreation Center Spray Park",
    address: "801 Taylor Street NW, Washington, DC 20011",
    ward: 4,
    lat: 38.94091,
    lng: -77.02338,
    maintenanceWindow: "Mondays 10am–2pm",
    confidence: "high",
  },
  {
    id: "riggs-lasalle-rec-spray",
    name: "Riggs-LaSalle Recreation Center Spray Park",
    address: "501 Riggs Road NE, Washington, DC 20011",
    ward: 4,
    lat: 38.96028,
    lng: -76.99932,
    maintenanceWindow: "Thursdays 2–6pm",
    confidence: "high",
  },
  {
    id: "takoma-playground-spray",
    name: "Takoma Playground Spray Park",
    address: "300 Van Buren Street NW, Washington, DC 20012",
    ward: 4,
    lat: 38.96986,
    lng: -77.01624,
    maintenanceWindow: "Tuesdays 2–6pm",
    confidence: "high",
  },
  {
    id: "edgewood-rec-spray",
    name: "Edgewood Recreation Center Spray Park",
    address: "3rd and Evarts Street NE, Washington, DC 20018",
    ward: 5,
    lat: 38.92450,
    lng: -77.00199,
    maintenanceWindow: "Mondays 10am–2pm",
    confidence: "medium",
  },
  {
    id: "cole-rec-spray",
    name: "Joseph H. Cole Recreation Center Spray Park",
    address: "1299 Neal Street NE, Washington, DC 20002",
    ward: 5,
    lat: 38.90335,
    lng: -76.98624,
    maintenanceWindow: "Tuesdays 2–6pm",
    confidence: "medium",
  },
  {
    id: "langdon-pool-spray",
    name: "Langdon Pool Deck Spray Park",
    address: "2860 Mills Avenue NE, Washington, DC 20018",
    ward: 5,
    lat: 38.92707,
    lng: -76.97307,
    maintenanceWindow: "Thursdays 10am–2pm",
    confidence: "medium",
  },
  {
    id: "reservoir-park-rec-spray",
    name: "Reservoir Park Recreation Center Spray Park",
    address: "North Capitol Street & Channing Street NW, Washington, DC",
    ward: 5,
    lat: 38.92237,
    lng: -77.00909,
    confidence: "medium",
  },
  {
    id: "turkey-thicket-rec-spray",
    name: "Turkey Thicket Recreation Center Spray Park",
    address: "1100 Michigan Avenue NE, Washington, DC 20017",
    ward: 5,
    lat: 38.93527,
    lng: -76.99294,
    confidence: "high",
  },
  {
    id: "eastern-market-metro-park-spray",
    name: "Eastern Market Metro Park Spray Park",
    address: "701 Pennsylvania Avenue SE, Washington, DC 20003",
    ward: 6,
    lat: 38.88481,
    lng: -76.99618,
    maintenanceWindow: "Mondays 10am–2pm",
    confidence: "high",
  },
  {
    id: "king-greenleaf-rec-spray",
    name: "King Greenleaf Recreation Center Spray Park",
    address: "201 N Street SW, Washington, DC 20024",
    ward: 6,
    lat: 38.87471,
    lng: -77.01245,
    maintenanceWindow: "Wednesdays 10am–2pm",
    confidence: "high",
  },
  {
    id: "potomac-ave-triangle-spray",
    name: "Potomac Avenue Triangle Park Spray Park",
    address: "1216 Potomac Avenue SE, Washington, DC 20003",
    ward: 6,
    lat: 38.87860,
    lng: -76.98992,
    confidence: "medium",
  },
  {
    id: "watkins-rec-spray",
    name: "Watkins Recreation Center Spray Park",
    address: "420 12th Street SE, Washington, DC 20003",
    ward: 6,
    lat: 38.88331,
    lng: -76.99017,
    maintenanceWindow: "Fridays 10am–2pm",
    confidence: "high",
  },
  {
    id: "benning-stoddert-rec-spray",
    name: "Benning Stoddert Recreation Center Spray Park",
    address: "100 Stoddert Place SE, Washington, DC 20019",
    ward: 7,
    lat: 38.88890,
    lng: -76.94820,
    maintenanceWindow: "Wednesdays 2–6pm",
    confidence: "medium",
  },
  {
    id: "hillcrest-rec-spray",
    name: "Hillcrest Recreation Center Spray Park",
    address: "3100 Denver Street SE, Washington, DC 20020",
    ward: 7,
    lat: 38.85841,
    lng: -76.96195,
    maintenanceWindow: "Wednesdays 2–6pm",
    confidence: "medium",
  },
  {
    id: "joy-evans-rec-spray",
    name: "Joy Evans Therapeutic Recreation Center Spray Park",
    address: "3030 G Street SE, Washington, DC",
    ward: 7,
    lat: 38.88029,
    lng: -76.96366,
    confidence: "low",
  },
  {
    id: "marvin-gaye-division-spray",
    name: "Marvin Gaye at Division Avenue Spray Park",
    address: "Division Avenue & Foote Street SE, Washington, DC",
    ward: 7,
    lat: 38.89717,
    lng: -76.92609,
    maintenanceWindow: "Wednesdays 10am–2pm",
    confidence: "medium",
  },
  {
    id: "marvin-gaye-rec-spray",
    name: "Marvin Gaye Recreation Center Spray Park",
    address: "6201 Banks Place NE, Washington, DC 20019",
    ward: 7,
    lat: 38.89191,
    lng: -76.91297,
    maintenanceWindow: "Wednesdays 2–6pm",
    confidence: "medium",
  },
  {
    id: "canal-park-spray",
    name: "Canal Park Spray Park",
    address: "200 M Street SE, Washington, DC 20003",
    ward: 6,
    lat: 38.87654,
    lng: -77.00346,
    maintenanceWindow: "Operated by Capitol Riverfront BID (Navy Yard BID); hours may differ",
    confidence: "high",
  },
];
